import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { styled, Grid, Dialog, Slider } from '@mui/material';
import React__default, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { PLACEHOLDER_GREEN_IMAGE } from '../../utils/constants/Constants.js';
import '../../icons/abstract/AbstractArcIcon.js';
import '../../icons/AddCircleIcon.js';
import '../../icons/AddCircleOutlineIcon.js';
import '../../icons/ArrowCircleRightIcon.js';
import '../../icons/ArrowLeftIcon.js';
import '../../icons/ArrowRightIcon.js';
import '../../icons/BoltIcon.js';
import '../../icons/BookmarkStarIcon.js';
import '../../icons/CalendarIcon.js';
import '../../icons/CancelIcon.js';
import '../../icons/CheckCircleIcon.js';
import '../../icons/CheckMarkIcon.js';
import '../../icons/ChevronDownIcon.js';
import '../../icons/ChevronLeftIcon.js';
import '../../icons/ChevronRightIcon.js';
import '../../icons/ChevronUpIcon.js';
import '../../icons/CloseIcon.js';
import '../../icons/MenuIcon.js';
import '../../icons/NotificationActiveIcon.js';
import '../../icons/PaperPlaneIcon.js';
import '../../icons/PendingIcon.js';
import '../../icons/PersonIcon.js';
import '../../icons/PlayCircleIcon.js';
import '../../icons/RemoveCircleIcon.js';
import '../../icons/SearchIcon.js';
import '../../icons/SellIcon.js';
import '../../icons/ShoppingBagIcon.js';
import '../../icons/SmartphoneIcon.js';
import '../../icons/StarIcon.js';
import '../../icons/StarOutlineIcon.js';
import '../../icons/SuccessCheckCircleIcon.js';
import '../../icons/TuneIcon.js';
import '../../icons/FourPointStarIcon.js';
import { Button } from '../Button/Button.js';
import '../Button/CustomDtcBagButton.js';

const cropWindow = {
    width: 400,
    height: 360,
};
const CropperContainer = styled('div')({
    width: cropWindow.width,
    padding: 25,
    '.crop': {
        position: 'relative',
        width: '100%',
        height: cropWindow.height,
        background: '#fff',
        borderRadius: 10,
        overflow: 'hidden',
    },
    '.control': {
        padding: '15px 0 0 10px',
        display: 'flex',
        alignItems: 'center',
    },
});
const LoadingContainer = styled(Grid, {
    shouldForwardProp: (prop) => !['aspectRatio'].includes(prop.toString()),
})(({ aspectRatio }) => ({
    width: cropWindow.width,
    height: cropWindow.height,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    img: {
        aspectRatio: `${aspectRatio}`,
        overflow: 'hidden',
    },
}));
const ImageCropper = ({ open, setOpen, file, fileTypeToUse, cropperProps, onFileChange, }) => {
    const [imageSrc, setImageSrc] = useState('');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [zoom, setZoom] = useState(1);
    const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);
    const cropIt = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (file) {
            try {
                const croppedImage = yield getCroppedImg(imageSrc, croppedAreaPixels, fileTypeToUse || (file === null || file === void 0 ? void 0 : file.type));
                const newFileEnding = (_a = fileTypeToUse === null || fileTypeToUse === void 0 ? void 0 : fileTypeToUse.split('/')) === null || _a === void 0 ? void 0 : _a[1]; // e.g. 'webp'
                const newFileName = newFileEnding // need to replace old file ending with new file ending
                    ? `${file.name.substring(0, file.name.lastIndexOf('.'))}.${newFileEnding}`
                    : file.name;
                const newFile = new File([croppedImage], newFileName, {
                    type: fileTypeToUse || (file === null || file === void 0 ? void 0 : file.type),
                });
                onFileChange(newFile);
                setOpen(false);
            }
            catch (e) {
                console.error('error cropping image: ', e);
            }
        }
    }), [imageSrc, croppedAreaPixels, file, onFileChange, setOpen]);
    //   const moveCrop = (x: number, y: number) => {
    //     setCrop({ x: crop.x + x, y: crop.y + y });
    //   };
    useEffect(() => {
        setCrop({ x: 0, y: 0 });
    }, [imageSrc]);
    useEffect(() => {
        const initImage = () => __awaiter(void 0, void 0, void 0, function* () {
            const imageDataUrl = yield readFile(file);
            setImageSrc(imageDataUrl);
        });
        if (file) {
            initImage();
        }
    }, [file]);
    return (React__default.createElement(Dialog, { open: open, onClose: () => {
            setOpen(false);
        }, maxWidth: "lg" },
        React__default.createElement(CropperContainer, null, imageSrc ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: "crop", onKeyDown: (e) => {
                    // eslint-disable-next-line no-console
                    console.log(e);
                } },
                React__default.createElement(Cropper, Object.assign({ image: imageSrc, crop: crop, zoom: zoom, onCropChange: setCrop, onZoomChange: setZoom, onCropComplete: onCropComplete, restrictPosition: false, showGrid: false }, cropperProps, { minZoom: 0.5 }))),
            React__default.createElement("div", { className: "control" },
                React__default.createElement("div", { style: { flex: 1, marginRight: 30 } },
                    React__default.createElement(Slider, { value: zoom, sx: (theme) => ({
                            color: theme.vars.palette.text.primary,
                        }), min: 0, max: 3, step: 0.1, "aria-labelledby": "Zoom", classes: {}, onChange: (e, zoom) => setZoom(zoom) })),
                React__default.createElement("div", null,
                    React__default.createElement(Button, { onClick: cropIt, variant: "solid", color: "primary" }, "Crop"))))) : (React__default.createElement(LoadingContainer, { aspectRatio: cropperProps.aspect },
            React__default.createElement("img", { src: PLACEHOLDER_GREEN_IMAGE['1X1'], alt: "loading-placeholder" }))))));
};
const readFile = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result), false);
        reader.readAsDataURL(file);
    });
};
const createImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
});
const getCroppedImg = (imageSrc, pixelCrop, type) => __awaiter(void 0, void 0, void 0, function* () {
    const image = yield createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.fillStyle = '#ffffff00';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
    // As a blob
    return new Promise((resolve) => {
        canvas.toBlob((file) => {
            resolve(file);
        }, type);
    });
});

export { ImageCropper };
//# sourceMappingURL=ImageCropper.js.map
